import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueInfo } from '@library/types/leagues';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { RankDrawerProjection } from '@bo-components/rankDrawer/RankDrawerProjection';
import type { Updater } from 'use-immer';
import { VirtualizedList } from '@library/components/virtualization/VirtualizedList';

interface RankDrawerProjectionsProps {
  isFetching: boolean;
  leagueInfo?: LeagueInfo;
  projections: Array<GamePlayerProjectionTrading>;
  setProjections: Updater<Array<GamePlayerProjectionTrading>>;
  showDuration: boolean;
}

const PLAYER_ITEM_HEIGHT = 48;

export const RankDrawerProjections = ({
  isFetching,
  leagueInfo = undefined,
  projections,
  setProjections,
  showDuration
}: RankDrawerProjectionsProps) => {
  const handleProjectionRankChange = ({
    targetProjectionId,
    value
  }: {
    targetProjectionId: string;
    value: string;
  }) => {
    const newRank = value !== '' ? Number(value) : undefined;

    if (
      (typeof newRank === 'number' && String(newRank).length < 6) ||
      typeof newRank === 'undefined'
    ) {
      setProjections((draft) => {
        const projectionIndex = draft.findIndex(
          (projection) => projection.projectionId === targetProjectionId
        );

        if (projectionIndex !== -1) {
          draft[projectionIndex].rank = newRank;
        }
      });
    }
  };

  const noResults = (
    <NoResults
      className="ml-2 h-full px-8"
      label="No Projections for this Stat Type. Please select another."
    />
  );

  return (
    <div className="overflow-hidden py-3">
      <div className="border-soFresh-130 relative mx-6 h-full overflow-hidden border py-2">
        {isFetching ? (
          <Loading loadingMessage="Loading Projections" className="h-full" />
        ) : (
          <VirtualizedList<GamePlayerProjectionTrading>
            keyGroup="rank-drawer-projection"
            items={projections}
            itemHeight={PLAYER_ITEM_HEIGHT}
            overscanRowCount={3}
            noItemsComponent={noResults}
          >
            {(projection, index) => {
              return index > 0 &&
                projection.startTime !== projections[index - 1].startTime ? (
                <RankDrawerProjection
                  className="border-soFresh-140 border-t"
                  handleProjectionRankChange={handleProjectionRankChange}
                  leagueInfo={leagueInfo}
                  projection={projection}
                  showDuration={showDuration}
                />
              ) : (
                <RankDrawerProjection
                  handleProjectionRankChange={handleProjectionRankChange}
                  leagueInfo={leagueInfo}
                  projection={projection}
                  showDuration={showDuration}
                />
              );
            }}
          </VirtualizedList>
        )}
      </div>
    </div>
  );
};
